import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { DateTime } from 'luxon';
import StatDateRange, { dubaiToday, shiftDays, useRangeParams } from './StatDateRange';
import './statCharts.css';

/**
 * Payments statistics (owner, 2026-09-09) — managers only; the server refuses
 * anyone else. What came in by day or week, by method (card online, bank
 * transfer in the portal, bank / cash / card recorded by hand in Zoho), this
 * period against the previous one day by day, which device the card payments
 * came from, and how saved cards are being used.
 *
 * Chart rules (dataviz): one axis per chart, categorical hues in a fixed
 * validated order, thin marks with a surface gap, legend for two or more
 * series, tooltip with the value first, a table view behind every chart, and
 * a refetch that dims the last render instead of flashing a skeleton.
 */
const COLORS = { card: '#2a78d6', bank: '#eb6834', cash: '#1baf7a', card_zoho: '#eda100', other: '#b3b1aa' };
// How an online checkout was paid — fixed order, same validated hues, never
// cycled: Apple Pay blue, Samsung Pay violet, new card orange, saved card aqua.
const RAIL_COLORS = { apple_pay: '#2a78d6', samsung_pay: '#4a3aa7', new_card: '#eb6834', saved_card: '#1baf7a', google_pay: '#eda100' };
const CHANNELS = { web: 'Web portal', ios: 'iOS app', android: 'Android app', unknown: 'Unknown (before device tracking)' };
const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (Math.abs(v) >= 10000) return `${Math.round(v / 1000)}K`;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return String(Math.round(v));
};
const fmtDay = (iso) => DateTime.fromISO(iso).toFormat('d MMM');
const fmtBucket = (iso, g) => (g === 'week' ? `w/c ${fmtDay(iso)}` : fmtDay(iso));

function ChartTip({ active, payload, label, title, series, withTotal = false }) {
    if (!active || !payload || !payload.length) return null;
    const rows = payload.filter((p) => p.value != null && Number(p.value) !== 0);
    if (!rows.length) return null;
    const sum = rows.reduce((t, p) => t + Number(p.value || 0), 0);
    return (
        <div className="sc-tip">
            <div className="sc-tipTitle">
                {typeof title === 'function' ? title(label, payload) : title}
                {withTotal && <span className="sc-tipTotal"> · Total {money(sum)}</span>}
            </div>
            {rows.map((p) => (
                <div className="sc-tipRow" key={p.dataKey}>
                    <span className="sc-tipKey"><span className="sc-swatch sc-swatch--line" style={{ background: p.color }} />{series[p.dataKey] || p.dataKey}</span>
                    <span className="sc-tipVal">{money(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * Part-to-whole as ONE bar: every share is a segment of the same 100 %, with a
 * 2 px surface gap between segments, and the exact figures in an aligned
 * table underneath — nothing wraps, nothing starts at a different x.
 */
function ShareBar({ rows, colorOf, nameOf, total, unit = 'amount', nameHeader = 'Method' }) {
    const live = rows.filter((r) => Number(r[unit]) > 0);
    const sum = live.reduce((s, r) => s + Number(r[unit]), 0) || 1;
    return (
        <div className="sc-share">
            <div className="sc-shareBar" role="img" aria-label="Share of the total">
                {live.map((r) => (
                    <div key={r.key} className="sc-shareSeg" style={{ width: `${(Number(r[unit]) / sum) * 100}%`, background: colorOf(r) }} title={`${nameOf(r)} · ${((Number(r[unit]) / sum) * 100).toFixed(0)}%`} />
                ))}
                {live.length === 0 && <div className="sc-shareSeg sc-shareSeg--empty" />}
            </div>
            <table className="sc-table sc-shareTable">
                <thead><tr><th></th><th>{nameHeader}</th><th className="sc-num">Amount</th><th className="sc-num">Payments</th><th className="sc-num">Share</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.key} className={Number(r[unit]) > 0 ? '' : 'sc-rowMuted'}>
                            <td className="sc-swatchCell"><span className="sc-swatch" style={{ background: colorOf(r) }} /></td>
                            <td>{nameOf(r)}</td>
                            <td className="sc-num"><b>{money(r.amount)}</b></td>
                            <td className="sc-num">{r.count}</td>
                            <td className="sc-num">{Number(r[unit]) > 0 ? `${((Number(r[unit]) / sum) * 100).toFixed(0)}%` : '—'}</td>
                        </tr>
                    ))}
                </tbody>
                {total != null && <tfoot><tr><td></td><td>Total</td><td className="sc-num"><b>{money(total)}</b></td><td className="sc-num">{rows.reduce((s, r) => s + Number(r.count || 0), 0)}</td><td className="sc-num">100%</td></tr></tfoot>}
            </table>
        </div>
    );
}

function Delta({ amount, pct }) {
    if (amount == null) return null;
    const cls = amount > 0.005 ? 'sc-delta--up' : amount < -0.005 ? 'sc-delta--down' : 'sc-delta--flat';
    const arrow = amount > 0.005 ? '▲' : amount < -0.005 ? '▼' : '—';
    return (
        <span className={`sc-delta ${cls}`}>
            {arrow} {money(Math.abs(amount))}{pct != null ? ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}
        </span>
    );
}

// Stable defaults: a new object per render would defeat the memo in useRangeParams.
const RANGE_DEFAULTS = { from: shiftDays(dubaiToday(), -29), to: dubaiToday(), granularity: 'day' };

export default function PaymentsStat() {
    const [range, setRange] = useRangeParams(RANGE_DEFAULTS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tables, setTables] = useState({});
    const toggleTable = (k) => setTables((t) => ({ ...t, [k]: !t[k] }));

    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        axios.get('/api/statistics/payments', { params: range })
            .then((res) => { if (alive) setData(res.data); })
            .catch((e) => {
                if (!alive) return;
                setError(e.response?.status === 403
                    ? 'Payment statistics are for managers only.'
                    : (e.response?.data?.error || 'Could not load payment statistics.'));
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [range]);

    const methodsPresent = useMemo(() => (data ? data.methods.filter((m) => m.count > 0) : []), [data]);
    const seriesKeys = methodsPresent.map((m) => m.key);
    // Rails with money in THIS range drive the chart and legend; the share
    // table below it always lists the four the owner asked for, at zero too.
    const railsPresent = useMemo(() => (data && data.checkout_rails ? data.checkout_rails.filter((r) => r.count > 0) : []), [data]);
    const railKeys = railsPresent.map((r) => r.key);
    const railLabels = data?.rail_labels || {};
    const labels = data?.method_labels || {};
    const previousLabel = data ? `${fmtDay(data.previous.from)} – ${fmtDay(data.previous.to)}` : '';

    return (
        <div className="sc-view">
            <StatDateRange value={range} onChange={setRange} withGranularity loading={loading} />
            {loading && data && <div className="sc-updating" role="status">Updating the figures for this range…</div>}
            {error && <div className="sc-empty">{error}</div>}
            {data && data.zoho && data.zoho.ok === false && (
                <div className="sc-warn" role="alert">
                    Zoho Books did not answer, so payments recorded by hand in Zoho are <b>missing</b> from these figures — card and portal bank transfers are complete. Press Apply again in a minute.
                </div>
            )}
            {!error && !data && <div className="sc-empty">Loading…</div>}
            {data && (
                <div className={loading ? 'sc-dim' : ''}>
                    {/* ── Headline tiles ─────────────────────────────────── */}
                    <div className="sc-tiles">
                        <div className="sc-tile sc-tile--hero">
                            <div className="sc-tileLabel">Collected · {data.range.days} day{data.range.days === 1 ? '' : 's'}</div>
                            <div className="sc-tileValue">{money(data.totals.amount)}</div>
                            <div className="sc-tileSub"><Delta amount={data.totals.delta_amount} pct={data.totals.delta_pct} /> vs previous {data.range.days} days</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Payments</div>
                            <div className="sc-tileValue">{data.totals.count.toLocaleString('en-US')}</div>
                            <div className="sc-tileSub">{data.previous.count.toLocaleString('en-US')} in the previous period</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Average per day</div>
                            <div className="sc-tileValue">{money(data.totals.avg_per_day)}</div>
                            <div className="sc-tileSub">{money(data.previous.amount / Math.max(1, data.range.days))} before</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Online Checkout share</div>
                            <div className="sc-tileValue">{(data.methods.find((m) => m.key === 'card')?.share || 0).toFixed(0)}%</div>
                            <div className="sc-tileSub">{money(data.methods.find((m) => m.key === 'card')?.amount || 0)} paid at the online checkout</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Paying clients</div>
                            <div className="sc-tileValue">{data.totals.paying_clients}</div>
                            <div className="sc-tileSub">distinct accounts that paid</div>
                        </div>
                    </div>

                    {/* ── Money in by method ─────────────────────────────── */}
                    <div className="sc-card">
                        <div className="sc-cardHead">
                            <div>
                                <h3 className="sc-cardTitle">Money in by method</h3>
                                <div className="sc-cardSub">Gross takings per {data.range.granularity}, before refunds · {fmtDay(data.range.from)} – {fmtDay(data.range.to)}</div>
                            </div>
                            <div className="sc-cardTools">
                                <button type="button" className={`sc-toggle${tables.methods ? ' is-active' : ''}`} onClick={() => toggleTable('methods')}>Table</button>
                            </div>
                        </div>
                        {seriesKeys.length > 0 && (
                            <div className="sc-legend">
                                {methodsPresent.map((m) => (
                                    <span className="sc-legendItem" key={m.key}><span className="sc-swatch" style={{ background: COLORS[m.key] }} />{m.label}</span>
                                ))}
                            </div>
                        )}
                        {data.totals.count === 0 ? (
                            <div className="sc-empty">No payments in this range.</div>
                        ) : tables.methods ? (
                            <div className="sc-tableWrap">
                                <table className="sc-table">
                                    <thead><tr><th>{data.range.granularity === 'week' ? 'Week' : 'Day'}</th>{methodsPresent.map((m) => <th className="sc-num" key={m.key}>{m.label}</th>)}<th className="sc-num">Total</th><th className="sc-num">Payments</th></tr></thead>
                                    <tbody>
                                        {data.series.map((b) => (
                                            <tr key={b.bucket}><td>{fmtBucket(b.bucket, data.range.granularity)}</td>{methodsPresent.map((m) => <td className="sc-num" key={m.key}>{b[m.key] ? money(b[m.key]) : '—'}</td>)}<td className="sc-num"><b>{money(b.total)}</b></td><td className="sc-num">{b.count}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="sc-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
                                        <CartesianGrid stroke="#e1e0d9" vertical={false} />
                                        <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, data.range.granularity)} tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} minTickGap={18} />
                                        <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={44} />
                                        <Tooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={<ChartTip series={labels} title={(l) => fmtBucket(l, data.range.granularity)} withTotal />} />
                                        {seriesKeys.map((k, i) => (
                                            <Bar key={k} dataKey={k} stackId="m" fill={COLORS[k]} stroke="#fcfcfb" strokeWidth={2} maxBarSize={30}
                                                radius={i === seriesKeys.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false} />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    <div className="sc-grid2">
                        {/* ── This period vs previous ─────────────────────── */}
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <div>
                                    <h3 className="sc-cardTitle">This period vs the previous one</h3>
                                    <div className="sc-cardSub">Day by day, all methods · previous = {previousLabel}</div>
                                </div>
                                <div className="sc-cardTools">
                                    <button type="button" className={`sc-toggle${tables.compare ? ' is-active' : ''}`} onClick={() => toggleTable('compare')}>Table</button>
                                </div>
                            </div>
                            <div className="sc-legend">
                                <span className="sc-legendItem"><span className="sc-swatch sc-swatch--line" style={{ background: '#2a78d6' }} />This period</span>
                                <span className="sc-legendItem"><span className="sc-swatch sc-swatch--line" style={{ background: '#b3b1aa' }} />Previous period</span>
                            </div>
                            {tables.compare ? (
                                <div className="sc-tableWrap">
                                    <table className="sc-table">
                                        <thead><tr><th>Day</th><th>This period</th><th className="sc-num">Amount</th><th>Previous</th><th className="sc-num">Amount</th></tr></thead>
                                        <tbody>
                                            {data.comparison.current.map((c, i) => { const p = data.comparison.previous[i]; return (
                                                <tr key={c.idx}><td>{c.idx}</td><td>{fmtDay(c.day)}</td><td className="sc-num">{money(c.total)}</td><td>{p ? fmtDay(p.day) : '—'}</td><td className="sc-num">{p ? money(p.total) : '—'}</td></tr>
                                            ); })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="sc-chart sc-chart--short">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={data.comparison.current.map((c, i) => ({ idx: c.idx, day: c.day, current: c.total, previous: data.comparison.previous[i]?.total ?? 0, prevDay: data.comparison.previous[i]?.day }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                            <CartesianGrid stroke="#e1e0d9" vertical={false} />
                                            <XAxis dataKey="idx" tickFormatter={(v) => `D${v}`} tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} minTickGap={16} />
                                            <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={44} />
                                            <Tooltip cursor={{ stroke: '#c3c2b7' }} content={<ChartTip series={{ current: 'This period', previous: 'Previous period' }} title={(l, p) => { const r = p?.[0]?.payload; return r ? `Day ${l} · ${fmtDay(r.day)} vs ${r.prevDay ? fmtDay(r.prevDay) : '—'}` : `Day ${l}`; }} />} />
                                            <Line type="monotone" dataKey="previous" stroke="#b3b1aa" strokeWidth={2} dot={false} activeDot={{ r: 5, stroke: '#fcfcfb', strokeWidth: 2 }} isAnimationActive={false} />
                                            <Line type="monotone" dataKey="current" stroke="#2a78d6" strokeWidth={2} dot={{ r: 3, stroke: '#fcfcfb', strokeWidth: 2, fill: '#2a78d6' }} activeDot={{ r: 5, stroke: '#fcfcfb', strokeWidth: 2 }} isAnimationActive={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* ── Methods share ───────────────────────────────── */}
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <div>
                                    <h3 className="sc-cardTitle">Where the money came from</h3>
                                    <div className="sc-cardSub">Share of {money(data.totals.amount)} in the range</div>
                                </div>
                            </div>
                            <ShareBar rows={data.methods} colorOf={(m) => COLORS[m.key]} nameOf={(m) => m.label} total={data.totals.amount} />
                            <div className="sc-note">Bank Transfer includes transfers approved in the portal and those recorded by hand in Zoho Books; anything the portal itself created in Zoho is counted once, under its own method.</div>
                        </div>
                    </div>

                    <div className="sc-grid2">
                        {/* ── Channels ────────────────────────────────────── */}
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <div>
                                    <h3 className="sc-cardTitle">Card payments by device</h3>
                                    <div className="sc-cardSub">Where the {data.saved_cards.card_payments} online card payment{data.saved_cards.card_payments === 1 ? '' : 's'} were made</div>
                                </div>
                            </div>
                            <ShareBar rows={data.channels} colorOf={(c) => ({ web: '#2a78d6', ios: '#eb6834', android: '#1baf7a', unknown: '#b3b1aa' }[c.key])} nameOf={(c) => CHANNELS[c.key]} unit="count" nameHeader="Device" />
                            <div className="sc-note">Device tracking on payments started in August 2026 — older payments show as unknown.</div>
                        </div>

                        {/* ── Saved cards ─────────────────────────────────── */}
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <div>
                                    <h3 className="sc-cardTitle">Saved cards</h3>
                                    <div className="sc-cardSub">Cards on file and how often they are used</div>
                                </div>
                            </div>
                            <div className="sc-tiles" style={{ marginBottom: 0 }}>
                                <div className="sc-tile"><div className="sc-tileLabel">Clients with a saved card</div><div className="sc-tileValue">{data.saved_cards.clients_with_card}</div><div className="sc-tileSub">{data.saved_cards.active_cards} active card{data.saved_cards.active_cards === 1 ? '' : 's'} in total</div></div>
                                <div className="sc-tile"><div className="sc-tileLabel">Saved in this range</div><div className="sc-tileValue">{data.saved_cards.saved_in_range}</div><div className="sc-tileSub">{data.saved_cards.saved_at_checkout_in_range} ticked "save my card" at checkout</div></div>
                                <div className="sc-tile"><div className="sc-tileLabel">Paid with a saved card</div><div className="sc-tileValue">{data.saved_cards.payments_with_saved_card}</div><div className="sc-tileSub">{data.saved_cards.share_of_card_payments.toFixed(0)}% of card payments · {money(data.saved_cards.amount_with_saved_card)}</div></div>
                            </div>
                        </div>
                    </div>
                    {/* ── Online checkout by payment method — last on the page (owner, 2026-09-10) ── */}
                    {data.checkout_rails && (
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <div>
                                    <h3 className="sc-cardTitle">Online checkout by payment method</h3>
                                    <div className="sc-cardSub">How the {data.checkout_total.count} online checkout payment{data.checkout_total.count === 1 ? '' : 's'} ({money(data.checkout_total.amount)}) were paid — Apple Pay, Samsung Pay, a new card or a saved card · per {data.range.granularity}</div>
                                </div>
                                <div className="sc-cardTools">
                                    <button type="button" className={`sc-toggle${tables.rails ? ' is-active' : ''}`} onClick={() => toggleTable('rails')}>Table</button>
                                </div>
                            </div>
                            {railKeys.length > 0 && (
                                <div className="sc-legend">
                                    {railsPresent.map((r) => (
                                        <span className="sc-legendItem" key={r.key}><span className="sc-swatch" style={{ background: RAIL_COLORS[r.key] }} />{r.label}</span>
                                    ))}
                                </div>
                            )}
                            {data.checkout_total.count === 0 ? (
                                <div className="sc-empty">No online checkout payments in this range.</div>
                            ) : tables.rails ? (
                                <div className="sc-tableWrap">
                                    <table className="sc-table">
                                        <thead><tr><th>{data.range.granularity === 'week' ? 'Week' : 'Day'}</th>{railsPresent.map((r) => <th className="sc-num" key={r.key}>{r.label}</th>)}<th className="sc-num">Total</th><th className="sc-num">Payments</th></tr></thead>
                                        <tbody>
                                            {data.rail_series.map((b) => (
                                                <tr key={b.bucket}><td>{fmtBucket(b.bucket, data.range.granularity)}</td>{railsPresent.map((r) => <td className="sc-num" key={r.key}>{b[r.key] ? money(b[r.key]) : '—'}</td>)}<td className="sc-num"><b>{money(b.total)}</b></td><td className="sc-num">{b.count}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="sc-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={data.rail_series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
                                            <CartesianGrid stroke="#e1e0d9" vertical={false} />
                                            <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, data.range.granularity)} tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} minTickGap={18} />
                                            <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={44} />
                                            <Tooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={<ChartTip series={railLabels} title={(l) => fmtBucket(l, data.range.granularity)} withTotal />} />
                                            {railKeys.map((k, i) => (
                                                <Bar key={k} dataKey={k} stackId="r" fill={RAIL_COLORS[k]} stroke="#fcfcfb" strokeWidth={2} maxBarSize={30}
                                                    radius={i === railKeys.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false} />
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            <ShareBar rows={data.checkout_rails} colorOf={(r) => RAIL_COLORS[r.key]} nameOf={(r) => r.label} total={data.checkout_total.amount} nameHeader="Paid with" />
                            <div className="sc-note">The wallet is the gateway's own word on each payment; saved card means a card kept on file in the portal was charged. Payments recorded by hand in Zoho are not online checkouts and are not counted here.</div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}
